import { useCallback, useEffect, useRef, useState } from 'react';

type Callback = () => void;

/**
 * @DEVNOTE Do not use in functional components
 * @see useDebounce
 */
export function debounce<T, A extends any[] = []>(
	callback: (...args: A) => T,
	delay: number = 250,
	unbouncedCallback?: Callback
): (...args: A) => void {
	let timeout: number;

	// @NOTE Cannot be an arrow function
	return function (...args) {
		const handler = () => callback.apply(this, args);

		if (typeof unbouncedCallback === 'function') {
			unbouncedCallback.call(this, args);
		}

		window.clearTimeout(timeout);
		timeout = window.setTimeout(handler, delay);
	};
}

export function useDebounce<T, A extends any[] = []>(
	callback: (...args: A) => T,
	delay: number = 250,
	deps: any[] = []
): (...args: A) => T {
	const timeout = useRef<number>(null);

	return useCallback(
		function (...args: A): T {
			if (timeout.current) {
				window.clearTimeout(timeout.current);
			}

			timeout.current = window.setTimeout(
				(): T => callback(...args) as T,
				delay
			);

			return;
		},
		[callback, ...deps]
	);
}

/** How long a search box waits after the last keystroke before it searches. */
export const SearchDelay = 350;

/**
 *	`value`, held back until it has stopped changing for `delay` ms. It starts
 *	as the value it is first given, so nothing waits on mount.
 */
export function useDebouncedValue<T = any>(value: T, delay: number = 250): T {
	const [debounced, setDebounced] = useState<T>(value);

	useEffect(() => {
		const timeout = window.setTimeout(() => setDebounced(value), delay);

		return () => window.clearTimeout(timeout);
	}, [value, delay]);

	return debounced;
}

export function delay(delay: number): Promise<void> {
	return new Promise((resolve: Callback) => window.setTimeout(resolve, delay));
}

export function useThrottle<T, A extends any[] = []>(
	callback: (...args: A) => T,
	interval: number = 250
): (...args: A) => void {
	const shouldCall = useRef<boolean>(true);
	const timeout = useRef<number>(null);

	return function (...args: A) {
		if (!shouldCall.current) return;

		shouldCall.current = false;
		callback.apply(this, ...args);

		timeout.current = window.setTimeout(
			() => (shouldCall.current = true),
			interval
		);
	};
}

/** How long `whenIdle` waits where the browser can't say when it is idle (Safari). */
const IdleFallbackMs: number = 2000;

/** How long `whenIdle` lets a browser that can say put the callback off at most. */
const IdleTimeoutMs: number = 5000;

/**
 *	Runs `callback` once the browser is idle, for work that can wait but is
 *	better done before it's needed. Returns a function that cancels it.
 */
export function whenIdle(callback: Callback): Callback {
	if (typeof window.requestIdleCallback === 'function') {
		const handle = window.requestIdleCallback(callback, {
			timeout: IdleTimeoutMs,
		});

		return () => window.cancelIdleCallback(handle);
	}

	const handle = window.setTimeout(callback, IdleFallbackMs);

	return () => window.clearTimeout(handle);
}
